<template>
  <div class="p-6">
    <!-- Step 1: Template Selection -->
    <div v-if="step === 'select_template'">
      <h2 class="text-lg font-semibold font-heading mb-4">Select a Template</h2>
      <p class="text-sm text-gray-500 mb-4">Choose a page template for publishing optimized content.</p>

      <div v-if="loadingTemplates" class="flex justify-center py-8">
        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
      </div>

      <div v-else class="grid grid-cols-2 gap-3">
        <button
          v-for="template in templates"
          :key="template.id"
          @click="selectTemplate(template)"
          class="rounded-xl border-2 p-4 text-left transition-colors hover:border-primary-500"
          :class="selectedTemplate?.id === template.id ? 'border-primary-500 bg-primary-50' : 'border-gray-200'"
        >
          <div class="font-medium text-sm">{{ template.name }}</div>
          <div class="text-xs text-gray-400 mt-1">{{ template.page_type }}</div>
        </button>
      </div>

      <div class="mt-6 flex justify-end">
        <button
          @click="startMapping"
          :disabled="!selectedTemplate || mapping"
          class="rounded-lg bg-green-500 px-4 py-2 text-sm font-medium text-white hover:bg-green-600 disabled:opacity-50"
        >
          <span v-if="mapping">Mapping Content...</span>
          <span v-else>Map Content to Template</span>
        </button>
      </div>
    </div>

    <!-- Step 2: Content Mapping Review -->
    <div v-if="step === 'review_mapping'">
      <h2 class="text-lg font-semibold font-heading mb-4">Review Content Mapping</h2>
      <p class="text-sm text-gray-500 mb-4">The LLM mapped your optimized content to template fields. Edit any values below.</p>

      <div class="space-y-4">
        <div v-for="(config, fieldName) in textFields" :key="fieldName">
          <label class="block text-sm font-medium text-gray-700 mb-1">
            {{ config.label || fieldName }}
          </label>
          <textarea
            v-if="config.type === 'textarea'"
            v-model="acfValues[fieldName]"
            class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
            rows="3"
          ></textarea>
          <input
            v-else
            v-model="acfValues[fieldName]"
            type="text"
            class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
          />
          <p v-if="!acfValues[fieldName]" class="text-xs text-amber-500 mt-1">Field is empty</p>
        </div>
      </div>

      <div class="mt-6 flex justify-between">
        <button @click="step = 'select_template'" class="rounded-lg border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50">
          Back
        </button>
        <button
          @click="generateImages"
          :disabled="generatingImages"
          class="rounded-lg bg-green-500 px-4 py-2 text-sm font-medium text-white hover:bg-green-600 disabled:opacity-50"
        >
          <span v-if="generatingImages">Generating Images...</span>
          <span v-else-if="hasImageFields">Generate Images & Preview</span>
          <span v-else>Preview</span>
        </button>
      </div>
    </div>

    <!-- Step 3: Image Preview & Publish -->
    <div v-if="step === 'preview'">
      <h2 class="text-lg font-semibold font-heading mb-4">Preview & Publish</h2>

      <!-- Image previews -->
      <div v-if="Object.keys(imageFields).length > 0" class="mb-6">
        <h3 class="text-sm font-medium text-gray-700 mb-3">Generated Images</h3>
        <div class="grid grid-cols-2 gap-4">
          <div v-for="(config, fieldName) in imageFields" :key="fieldName" class="relative">
            <div class="text-xs text-gray-500 mb-1">{{ config.label || fieldName }}</div>
            <div v-if="images[fieldName]?.url" class="relative group">
              <img :src="images[fieldName].url" class="w-full rounded-lg border" :alt="config.label" />
              <button
                @click="regenerateImage(fieldName)"
                class="absolute bottom-2 right-2 rounded-lg bg-white/90 px-2 py-1 text-xs font-medium shadow hover:bg-white opacity-0 group-hover:opacity-100 transition-opacity"
              >
                Regenerate
              </button>
            </div>
            <div v-else-if="images[fieldName]?.error" class="rounded-lg border border-red-200 bg-red-50 p-4 text-center">
              <p class="text-sm text-red-600">Image generation failed</p>
              <button @click="regenerateImage(fieldName)" class="mt-2 text-xs text-primary-500 hover:underline">
                Retry
              </button>
            </div>
            <div v-else class="rounded-lg border border-gray-200 bg-gray-50 p-4 text-center">
              <div class="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-500 mx-auto"></div>
              <p class="text-xs text-gray-400 mt-2">Generating...</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Publish button -->
      <div class="mt-6 flex justify-between">
        <button @click="step = 'review_mapping'" class="rounded-lg border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50">
          Back
        </button>
        <button
          @click="confirmPublish"
          :disabled="publishing"
          class="rounded-lg bg-green-500 px-4 py-2 text-sm font-medium text-white hover:bg-green-600 disabled:opacity-50"
        >
          <span v-if="publishing">Publishing...</span>
          <span v-else>Confirm & Publish</span>
        </button>
      </div>
    </div>

    <!-- Step 4: Success -->
    <div v-if="step === 'published'" class="text-center py-12">
      <div class="text-green-500 text-4xl mb-4">&#10003;</div>
      <h2 class="text-lg font-semibold font-heading mb-2">Published Successfully</h2>
      <p class="text-sm text-gray-500">Page has been published to WordPress.</p>
    </div>

    <!-- Error display -->
    <div v-if="error" class="mt-4 rounded-lg bg-red-50 border border-red-200 p-3">
      <p class="text-sm text-red-600">{{ error }}</p>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { getPageTemplates } from '~/javascript/api/page_templates'
import { publishToWp, generatePublishImages, regeneratePublishImage, confirmPublishToWp } from '~/javascript/api/website_pages'

const props = defineProps({
  websitePageId: { type: String, required: true },
  websiteId: { type: String, required: true }
})

const emit = defineEmits(['close', 'published'])

const step = ref('select_template')
const templates = ref([])
const loadingTemplates = ref(false)
const selectedTemplate = ref(null)
const mapping = ref(false)
const generatingImages = ref(false)
const publishing = ref(false)
const error = ref(null)

const pageBuildId = ref(null)
const acfValues = ref({})
const templateSchema = ref({})
const images = ref({})

const textFields = computed(() => {
  const schema = templateSchema.value || {}
  return Object.fromEntries(
    Object.entries(schema).filter(([, config]) => config.type !== 'image')
  )
})

const imageFields = computed(() => {
  const schema = templateSchema.value || {}
  return Object.fromEntries(
    Object.entries(schema).filter(([, config]) => config.type === 'image')
  )
})

const hasImageFields = computed(() => Object.keys(imageFields.value).length > 0)

onMounted(async () => {
  await loadTemplates()
})

async function loadTemplates() {
  loadingTemplates.value = true
  try {
    const response = await getPageTemplates(props.websiteId)
    templates.value = response.data
  } catch (e) {
    error.value = 'Failed to load templates'
  } finally {
    loadingTemplates.value = false
  }
}

function selectTemplate(template) {
  selectedTemplate.value = template
}

async function startMapping() {
  mapping.value = true
  error.value = null
  try {
    const response = await publishToWp(props.websitePageId, {
      template_id: selectedTemplate.value.id
    })
    pageBuildId.value = response.data.page_build_id
    acfValues.value = response.data.proposed_acf_values || {}
    templateSchema.value = response.data.template_schema || {}
    step.value = 'review_mapping'
  } catch (e) {
    error.value = e.response?.data?.error || 'Content mapping failed'
  } finally {
    mapping.value = false
  }
}

async function generateImages() {
  if (!hasImageFields.value) {
    step.value = 'preview'
    return
  }

  generatingImages.value = true
  error.value = null
  step.value = 'preview'

  try {
    const response = await generatePublishImages(props.websitePageId, {
      page_build_id: pageBuildId.value,
      acf_values: acfValues.value
    })
    images.value = response.data.images || {}
  } catch (e) {
    error.value = e.response?.data?.error || 'Image generation failed'
  } finally {
    generatingImages.value = false
  }
}

async function regenerateImage(fieldName) {
  images.value[fieldName] = null
  try {
    const response = await regeneratePublishImage(props.websitePageId, {
      page_build_id: pageBuildId.value,
      field_name: fieldName
    })
    images.value[fieldName] = { url: response.data.url, error: response.data.error }
  } catch (e) {
    images.value[fieldName] = { error: 'Regeneration failed' }
  }
}

async function confirmPublish() {
  publishing.value = true
  error.value = null
  try {
    await confirmPublishToWp(props.websitePageId, {
      page_build_id: pageBuildId.value,
      acf_values: acfValues.value
    })
    step.value = 'published'
    emit('published')
  } catch (e) {
    error.value = e.response?.data?.error || 'Publishing failed'
  } finally {
    publishing.value = false
  }
}
</script>
